import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Fish, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/StatCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const empty = { name: "", latin_name: "", sizes: "", description: "", shelf_life_days: 180 };

export default function MasterSpecies() {
  const { can } = useAuth(); const isAdmin = can("master_manage");
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null); // null | {item?, data}
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/species").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => setForm({ item: null, data: empty });
  const openEdit = (s) => setForm({ item: s, data: { ...s, sizes: s.sizes.join(", "), shelf_life_days: s.shelf_life_days || 180 } });
  const set = (k, v) => setForm((p) => ({ ...p, data: { ...p.data, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { name: form.data.name, latin_name: form.data.latin_name, description: form.data.description, shelf_life_days: Number(form.data.shelf_life_days) || 180,
      sizes: form.data.sizes.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (form.item) await api.put(`/species/${form.item.id}`, payload); else await api.post("/species", payload);
      toast.success("Jenis ikan disimpan"); setForm(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Hapus jenis ikan ${s.name}?`)) return;
    try { await api.delete(`/species/${s.id}`); toast.success("Jenis ikan dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="species-page">
      <PageHeader title="Master Jenis Ikan & Size" subtitle="Daftar jenis ikan laut beserta standar ukuran (size) yang tersedia">
        {isAdmin && <Button className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={openNew} data-testid="btn-add-species"><Plus className="h-4 w-4 mr-1" /> Tambah Jenis</Button>}
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((s, idx) => (
          <div key={s.id} className="card-aqua p-5 rise" style={{ animationDelay: `${idx * 0.05}s` }} data-testid={`species-card-${idx}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center"><Fish className="h-5 w-5" /></div>
                <div>
                  <div className="font-bold text-slate-900">{s.name}</div>
                  <div className="text-xs italic text-slate-500">{s.latin_name || "—"}</div>
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-sky-700" onClick={() => openEdit(s)} data-testid={`btn-edit-species-${idx}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => remove(s)} data-testid={`btn-delete-species-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {s.sizes.map((z) => <span key={z} className="px-2.5 py-1 rounded-full bg-sky-50 border border-sky-200 text-xs font-semibold text-sky-800">{z}</span>)}
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-500" data-testid={`species-shelf-life-${idx}`}><CalendarClock className="h-3.5 w-3.5 text-sky-600" /> Masa simpan <b className="text-slate-700">{s.shelf_life_days || 180} hari</b></div>
            {s.description && <p className="mt-3 text-sm text-slate-500">{s.description}</p>}
          </div>
        ))}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="species-form-dialog">
          <DialogHeader><DialogTitle>{form?.item ? "Edit Jenis Ikan" : "Tambah Jenis Ikan"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5"><Label>Nama Ikan</Label><Input value={form.data.name} onChange={(e) => set("name", e.target.value)} required placeholder="cth. Cakalang" data-testid="input-species-name" /></div>
              <div className="space-y-1.5"><Label>Nama Latin</Label><Input value={form.data.latin_name} onChange={(e) => set("latin_name", e.target.value)} placeholder="Opsional" data-testid="input-species-latin" /></div>
              <div className="space-y-1.5"><Label>Daftar Size (pisahkan dengan koma)</Label><Input value={form.data.sizes} onChange={(e) => set("sizes", e.target.value)} required placeholder="1-2 kg, 2-4 kg, 4 kg UP" data-testid="input-species-sizes" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Masa Simpan (hari)</Label><Input type="number" min="1" value={form.data.shelf_life_days} onChange={(e) => set("shelf_life_days", e.target.value)} required data-testid="input-species-shelf-life" /></div>
                <div className="space-y-1.5"><Label>Deskripsi</Label><Input value={form.data.description} onChange={(e) => set("description", e.target.value)} placeholder="Opsional" data-testid="input-species-desc" /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForm(null)}>Batal</Button>
                <Button type="submit" disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-species-submit">{saving ? "Menyimpan..." : "Simpan"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
